import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, AlertOctagon, Workflow } from "lucide-react";
import CTABand from "../components/CTABand";
import { MEDIA } from "../lib/constants";
import { useSEO } from "../lib/seo";

export default function MedicaidAR() {
  useSEO({
    title:
      "Medicaid Pending Recovery & AR Consulting · Missouri SNF",
    description:
      "Recover Medicaid pending revenue and reduce AR exposure across Missouri skilled nursing facilities. State-specific submission protocols, weekly accountability cadence, and 30/60/90 escalation pathways.",
    path: "/medicaid-ar-recovery",
  });
  const points = [
    "Intake and eligibility verification rebuilt to a 24 hour standard",
    "Named owner per case with a documented escalation pathway",
    "Weekly pending huddle that closes the accountability gap",
    "State specific submission protocols, not generic checklists",
    "30, 60, and 90 day escalation framework tied to ownership review",
    "AR aging dashboards that surface risk before it compounds",
  ];

  return (
    <div data-testid="medicaid-ar-page" className="bg-ivory">
      {/* HERO */}
      <section className="section-pad pt-32 relative overflow-hidden bg-paper">
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="overline mb-5">Practice Area · Medicaid Pending and AR Recovery</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
                Stop the bleed.<br />
                <span className="italic text-teal">Recover the revenue.</span>
              </h1>
              <p className="font-sans text-lg text-navy/75 max-w-2xl leading-relaxed">
                Medicaid pending and AR aging are leading indicators of nearly
                every other operational risk a facility carries. AthenaCrest
                rebuilds the workflow that recovers the dollars, then installs
                the accountability that keeps them recovered.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-px bg-champagne-200 border border-champagne-200">
                {[
                  { k: "30+", v: "Days pending we typically inherit" },
                  { k: "7 figs", v: "AR exposure we routinely recover" },
                  { k: "5 wks", v: "Avg time to first material recovery" },
                  { k: "50-state", v: "Submission protocol library" },
                ].map((s) => (
                  <div key={s.v} className="bg-ivory p-6">
                    <div className="font-serif text-3xl text-navy">{s.k}</div>
                    <div className="text-xs uppercase tracking-overline text-gold mt-2">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="section-pad" data-testid="problem-section">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-5">The Symptom</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-navy leading-tight">
              The pending balance keeps climbing, and no one owns it.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 font-sans text-navy/80 leading-relaxed">
            <p>
              Admissions hands the application to social services. Social
              services hands it to the business office. The business office
              hands it back to admissions. The patient sits in pending for 60+
              days while the facility absorbs the cost of care.
            </p>
            <p>
              This isn't a paperwork problem. It's a workflow accountability
              problem, and it costs facilities seven figures a year in slow or
              uncollected revenue. The same workflow weakness shows up in
              private-pay AR, managed care denials, and the cash-flow
              conversations that follow.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE INSTALL */}
      <section className="bg-navy text-ivory section-pad relative overflow-hidden" data-testid="install-section">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${MEDIA.hospitalHallway})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.4)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
        <div className="container-luxe relative z-10">
          <div className="max-w-3xl mb-12">
            <div className="overline mb-5">What We Install</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-ivory leading-[1.05]">
              A Medicaid and AR workflow built for{" "}
              <span className="italic text-gold">predictable recovery.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-5">
            {points.map((p, i) => (
              <div key={p} className="flex items-start gap-4 border-b border-ivory/15 pb-4">
                <span className="font-serif text-gold text-lg">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-sans text-base text-ivory/85 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="medicaid-cta-band"
        eyebrow="Recover the AR"
        headline="Bring AthenaCrest into the AR conversation before the quarter closes."
        sub="A 30 minute consultation surfaces the highest leverage pending and AR exposure, along with the engagement that recovers it."
      />
    </div>
  );
}
